/** Modo apresentação: esconde da tela o que é individualizado e constrangedor.
 *
 * Serve para projetar o portal em sessão sem expor quem está devendo. O que
 * some é o dado **por irmão** (saldo, situação, mensalidade em aberto); o que
 * fica são os totais e as contagens, que são justamente o que se apresenta.
 *
 * A máscara é de apresentação, não de autorização: os valores continuam no
 * navegador de quem já tinha permissão para vê-los. Quem abrir as ferramentas
 * de desenvolvedor lê tudo. Serve para não constranger ninguém na projeção —
 * não para mostrar a tela a quem não podia ver.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const CHAVE = 'portal:modo-apresentacao';

interface Contexto {
  ativo: boolean;
  alternar: () => void;
}

const ContextoApresentacao = createContext<Contexto>({ ativo: false, alternar: () => {} });

export function ProvedorApresentacao({ children }: { children: ReactNode }) {
  // Sobrevive ao recarregar: perder a máscara no meio de uma projeção por
  // causa de um F5 é exatamente o acidente que este modo evita.
  const [ativo, setAtivo] = useState(() => localStorage.getItem(CHAVE) === '1');

  const alternar = useCallback(() => setAtivo((v) => !v), []);

  useEffect(() => {
    localStorage.setItem(CHAVE, ativo ? '1' : '0');
    // A classe no <html> permite mascarar via CSS sem cada tela saber do modo.
    document.documentElement.classList.toggle('modo-apresentacao', ativo);
  }, [ativo]);

  // Atalho para alternar sem procurar o botão com a sala olhando.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        alternar();
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [alternar]);

  return (
    <ContextoApresentacao.Provider value={{ ativo, alternar }}>
      {children}
    </ContextoApresentacao.Provider>
  );
}

export function useApresentacao(): Contexto {
  return useContext(ContextoApresentacao);
}

/**
 * Envolve um valor individualizado. No modo apresentação sai borrado.
 *
 * O conteúdo continua no DOM de propósito: preservar a largura evita que a
 * tabela dance ao ligar e desligar o modo, e a leitura por trás do borrão é
 * aceitável para o caso de uso (projeção), não para sigilo.
 */
export function Sigiloso({
  children,
  rotulo = 'oculto no modo apresentação',
  intensidade = 'normal',
}: {
  children: ReactNode;
  rotulo?: string;
  /** `forte` para nomes em listas de inadimplência, onde o formato entrega. */
  intensidade?: 'normal' | 'forte';
}) {
  const { ativo } = useApresentacao();
  if (!ativo) return <>{children}</>;
  return (
    <span
      className={`sigiloso${intensidade === 'forte' ? ' sigiloso-forte' : ''}`}
      title={rotulo}
      aria-label={rotulo}
    >
      {children}
    </span>
  );
}
